import React, { useCallback, useEffect, useState } from "react";
import { getLiveAttendanceStatus } from "../../api/attendance";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import { formatAttendanceTime } from "../../utils/attendanceTimeFormat";
import { isFeatureAccessible } from "../../utils/featureVisibility";
import "./LiveAttendanceBoard.css";

const COLUMNS = [
  {
    key: "present",
    title: "حاضر",
    hint: "سجّل حضور النهاردة",
    cls: "is-present",
  },
  {
    key: "notArrived",
    title: "لسه مجاش",
    hint: "متوقع ولسه جوه الميعاد",
    cls: "is-waiting",
  },
  {
    key: "absent",
    title: "غايب",
    hint: "عدّى الميعاد من غير حضور",
    cls: "is-absent",
  },
];

function personMeta(row) {
  const place = row.branchName || "";
  if (row.status === "present" && row.checkInAt) {
    return [formatAttendanceTime(row.checkInAt), place].filter(Boolean).join(" · ");
  }
  const shift =
    row.scheduledStart && row.scheduledEnd
      ? `${row.scheduledStart} — ${row.scheduledEnd}`
      : "";
  return [shift, place].filter(Boolean).join(" · ");
}

export default function ManagerTeamAttendance() {
  const flags = useFeatureFlags();
  const enabled = isFeatureAccessible(flags, "attendance");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getLiveAttendanceStatus({ scope: "team" });
      setData(result);
      setError(null);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "تعذر تحميل حضور الفريق";
      setError(Array.isArray(message) ? message.join("\n") : message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    load();
    const timer = setInterval(load, 60_000);
    return () => clearInterval(timer);
  }, [enabled, load]);

  if (!enabled) return null;

  return (
    <section className="live-att-board mgr-team-att" dir="rtl">
      <div className="live-att-head">
        <div>
          <h2 className="live-att-title">حضور الفريق</h2>
          <p className="live-att-sub">
            مين حاضر · مين لسه مجاش · مين غايب
            {data?.date ? ` · ${data.date}` : ""}
          </p>
        </div>
        <button
          type="button"
          className="live-att-btn"
          onClick={load}
          disabled={loading}
        >
          تحديث
        </button>
      </div>

      {error ? <p className="live-att-error">{error}</p> : null}

      <div className="mgr-team-columns">
        {COLUMNS.map((column) => {
          const rows = data?.[column.key] || [];
          const count = data?.counts?.[column.key];
          return (
            <div key={column.key} className={`mgr-team-col ${column.cls}`}>
              <div className="mgr-team-col-head">
                <span className="live-att-summary-dot" aria-hidden="true" />
                <span className="mgr-team-col-title">{column.title}</span>
                <strong>{loading && count == null ? "…" : count ?? 0}</strong>
              </div>
              <p className="mgr-team-col-hint">{column.hint}</p>
              <div className="mgr-team-col-list">
                {loading && !data ? (
                  <p className="live-att-empty">جاري التحميل…</p>
                ) : rows.length === 0 ? (
                  <p className="live-att-empty">لا يوجد</p>
                ) : (
                  rows.map((row) => (
                    <div key={row.employeeId} className="mgr-team-person">
                      <span className="mgr-team-person-name">{row.fullName}</span>
                      <span className="mgr-team-person-meta">
                        {row.jobTitle ? `${row.jobTitle}` : ""}
                        {personMeta(row)
                          ? `${row.jobTitle ? " · " : ""}${personMeta(row)}`
                          : ""}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
